// Group Chat API - Database-backed group messaging
import { turso } from '@/database/client';

export interface Group {
  id: string;
  name: string;
  created_by: string;
  created_at: string;
  is_public?: boolean;
  member_count?: number;
  last_message?: string;
  user_is_member?: boolean;
  has_pending_request?: boolean;
}

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  user_name: string;
  joined_at: string;
}

export interface JoinRequest {
  id: string;
  group_id: string;
  user_id: string;
  user_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface GroupMessage {
  id: string;
  group_id: string;
  user_id: string;
  user_name: string;
  message: string;
  created_at: string;
}

// Create a new group
export async function createGroup(name: string, createdBy: string, memberIds: string[]): Promise<Group | null> {
  try {
    const groupId = `grp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log('[GroupChat] Creating group:', { groupId, name, createdBy, memberIds });
    
    // Create group
    await turso.execute({
      sql: `INSERT INTO groups (id, name, created_by) VALUES (?, ?, ?)`,
      args: [groupId, name, createdBy]
    });
    console.log('[GroupChat] Group record created');

    // Add creator as member
    const creatorMemberId = `gm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await turso.execute({
      sql: `INSERT INTO group_members (id, group_id, user_id) VALUES (?, ?, ?)`,
      args: [creatorMemberId, groupId, createdBy]
    });
    console.log('[GroupChat] Creator added as member');

    // Add other members
    for (const userId of memberIds) {
      if (userId !== createdBy) {
        const memberId = `gm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        await turso.execute({
          sql: `INSERT INTO group_members (id, group_id, user_id) VALUES (?, ?, ?)`,
          args: [memberId, groupId, userId]
        });
        console.log('[GroupChat] Added member:', userId);
      }
    }

    // Fetch created group
    const result = await turso.execute({
      sql: `SELECT * FROM groups WHERE id = ?`,
      args: [groupId]
    });

    if (result.rows.length > 0) {
      const row: any = result.rows[0];
      const group = {
        id: row.id,
        name: row.name,
        created_by: row.created_by,
        created_at: row.created_at,
        member_count: memberIds.length + 1
      };
      console.log('[GroupChat] ✅ Group created successfully:', group);
      return group;
    }

    console.error('[GroupChat] ❌ Group created but not found in database');
    return null;
  } catch (error) {
    console.error('[GroupChat] ❌ Failed to create group:', error);
    return null;
  }
}

// Get ALL public groups (visible to everyone) with membership status
// Works with BOTH user ID systems: entities table (numeric) and users table (UUID)
export async function getAllPublicGroups(userId: string): Promise<Group[]> {
  try {
    console.log('[GroupChat] Loading all public groups for user:', userId);
    
    // First, try to find the user's UUID from users table if they provided an entity ID
    let userUuid = userId;
    let entityId = userId;
    
    // Check if userId is numeric (entity ID) - need to find corresponding UUID
    if (/^\d+$/.test(userId)) {
      console.log('[GroupChat] Detected numeric entity ID, looking up UUID...');
      const userResult = await turso.execute({
        sql: `
          SELECT u.id as uuid_id
          FROM users u
          INNER JOIN entities e ON e.data LIKE '%' || u.email || '%'
          WHERE e.id = ? AND e.entity_type = 'User'
          LIMIT 1
        `,
        args: [userId]
      });
      
      if (userResult.rows.length > 0) {
        userUuid = userResult.rows[0].uuid_id as string;
        console.log('[GroupChat] Found UUID:', userUuid, 'for entity ID:', userId);
      }
    } else {
      // UUID provided, try to find entity ID
      console.log('[GroupChat] Using UUID, attempting to find entity ID...');
      const entityResult = await turso.execute({
        sql: `
          SELECT e.id as entity_id
          FROM entities e
          INNER JOIN users u ON e.data LIKE '%' || u.email || '%'
          WHERE u.id = ? AND e.entity_type = 'User'
          LIMIT 1
        `,
        args: [userId]
      });
      
      if (entityResult.rows.length > 0) {
        entityId = entityResult.rows[0].entity_id as string;
        console.log('[GroupChat] Found entity ID:', entityId, 'for UUID:', userId);
      }
    }
    
    const result = await turso.execute({
      sql: `
        SELECT 
          g.id, 
          g.name, 
          g.created_by, 
          g.created_at,
          COUNT(DISTINCT gm.user_id) as member_count,
          (SELECT message FROM group_messages WHERE group_id = g.id ORDER BY created_at DESC LIMIT 1) as last_message,
          EXISTS(SELECT 1 FROM group_members WHERE group_id = g.id AND (user_id = ? OR user_id = ?)) as user_is_member,
          EXISTS(SELECT 1 FROM group_join_requests WHERE group_id = g.id AND (user_id = ? OR user_id = ?) AND status = 'pending') as has_pending_request
        FROM groups g
        LEFT JOIN group_members gm ON g.id = gm.group_id
        GROUP BY g.id
        ORDER BY g.created_at DESC
      `,
      args: [userUuid, entityId, userUuid, entityId]
    });

    console.log('[GroupChat] Found', result.rows.length, 'total groups');
    const groups = result.rows.map((row: any) => ({
      id: row.id,
      name: row.name,
      created_by: row.created_by,
      created_at: row.created_at,
      is_public: true,
      member_count: Number(row.member_count) || 0,
      last_message: row.last_message,
      user_is_member: Boolean(row.user_is_member),
      has_pending_request: Boolean(row.has_pending_request)
    }));
    
    console.log('[GroupChat] Returning groups:', groups);
    return groups;
  } catch (error) {
    console.error('Failed to get public groups:', error);
    return [];
  }
}

// Legacy function for backward compatibility
export async function getUserGroups(userId: string): Promise<Group[]> {
  return getAllPublicGroups(userId);
}

// Get group members
export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT gm.id, gm.group_id, gm.user_id, gm.joined_at, u.name as user_name
        FROM group_members gm
        INNER JOIN users u ON gm.user_id = u.id
        WHERE gm.group_id = ?
        ORDER BY gm.joined_at ASC
      `,
      args: [groupId]
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      group_id: row.group_id,
      user_id: row.user_id,
      user_name: row.user_name,
      joined_at: row.joined_at
    }));
  } catch (error) {
    console.error('Failed to get group members:', error);
    return [];
  }
}

// Send message to group
export async function sendGroupMessage(groupId: string, userId: string, messageText: string): Promise<GroupMessage | null> {
  try {
    const messageId = `gmsg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    await turso.execute({
      sql: `INSERT INTO group_messages (id, group_id, user_id, message) VALUES (?, ?, ?, ?)`,
      args: [messageId, groupId, userId, messageText]
    });

    // Fetch created message with user name
    const result = await turso.execute({
      sql: `
        SELECT gm.*, u.name as user_name
        FROM group_messages gm
        INNER JOIN users u ON gm.user_id = u.id
        WHERE gm.id = ?
      `,
      args: [messageId]
    });

    if (result.rows.length > 0) {
      const row: any = result.rows[0];
      return {
        id: row.id,
        group_id: row.group_id,
        user_id: row.user_id,
        user_name: row.user_name,
        // Normalize to string to avoid React object render crashes
        message: typeof row.message === 'string' ? row.message : JSON.stringify(row.message),
        created_at: row.created_at
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to send group message:', error);
    return null;
  }
}

// Get group messages
export async function getGroupMessages(groupId: string): Promise<GroupMessage[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT gm.*, u.name as user_name
        FROM group_messages gm
        INNER JOIN users u ON gm.user_id = u.id
        WHERE gm.group_id = ?
        ORDER BY gm.created_at ASC
      `,
      args: [groupId]
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      group_id: row.group_id,
      user_id: row.user_id,
      user_name: row.user_name,
      // Normalize to string so UI never receives objects
      message: typeof row.message === 'string' ? row.message : JSON.stringify(row.message),
      created_at: row.created_at
    }));
  } catch (error) {
    console.error('Failed to get group messages:', error);
    return [];
  }
}

// Delete group (only creator can delete)
export async function deleteGroup(groupId: string, userId: string): Promise<boolean> {
  try {
    // Check if user is the creator
    const groupResult = await turso.execute({
      sql: `SELECT created_by FROM groups WHERE id = ?`,
      args: [groupId]
    });

    if (groupResult.rows.length === 0) {
      return false;
    }

    const group: any = groupResult.rows[0];
    if (group.created_by !== userId) {
      console.error('Only group creator can delete group');
      return false;
    }

    // Delete group (cascade will delete members and messages)
    await turso.execute({
      sql: `DELETE FROM groups WHERE id = ?`,
      args: [groupId]
    });

    return true;
  } catch (error) {
    console.error('Failed to delete group:', error);
    return false;
  }
}

// Leave group
export async function leaveGroup(groupId: string, userId: string): Promise<boolean> {
  try {
    await turso.execute({
      sql: `DELETE FROM group_members WHERE group_id = ? AND user_id = ?`,
      args: [groupId, userId]
    });
    return true;
  } catch (error) {
    console.error('Failed to leave group:', error);
    return false;
  }
}

// Remove member from group (creator only)
export async function removeMemberFromGroup(groupId: string, creatorId: string, memberIdToRemove: string): Promise<boolean> {
  try {
    console.log('[GroupChat] Creator removing member:', { groupId, creatorId, memberIdToRemove });
    
    // Verify the user is the creator
    const groupResult = await turso.execute({
      sql: `SELECT created_by FROM groups WHERE id = ?`,
      args: [groupId]
    });
    
    if (groupResult.rows.length === 0) {
      console.error('[GroupChat] Group not found');
      return false;
    }
    
    const group: any = groupResult.rows[0];
    if (group.created_by !== creatorId) {
      console.error('[GroupChat] Only group creator can remove members');
      return false;
    }
    
    // Can't remove the creator
    if (memberIdToRemove === creatorId) {
      console.error('[GroupChat] Cannot remove group creator');
      return false;
    }
    
    // Remove the member
    await turso.execute({
      sql: `DELETE FROM group_members WHERE group_id = ? AND user_id = ?`,
      args: [groupId, memberIdToRemove]
    });
    
    console.log('[GroupChat] ✅ Member removed from group');
    return true;
  } catch (error) {
    console.error('[GroupChat] Failed to remove member:', error);
    return false;
  }
}

// Add member to existing group (for join requests or invites)
export async function addGroupMember(groupId: string, userId: string): Promise<boolean> {
  try {
    // Check if already a member
    const existing = await turso.execute({
      sql: `SELECT id FROM group_members WHERE group_id = ? AND user_id = ?`,
      args: [groupId, userId]
    });

    if (existing.rows.length > 0) {
      console.log('[GroupChat] User already a member');
      return true; // Already a member
    }

    const memberId = `gm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await turso.execute({
      sql: `INSERT INTO group_members (id, group_id, user_id) VALUES (?, ?, ?)`,
      args: [memberId, groupId, userId]
    });
    
    console.log('[GroupChat] Added member to group');
    return true;
  } catch (error) {
    console.error('Failed to add group member:', error);
    return false;
  }
}

// Request to join a group
export async function requestJoinGroup(groupId: string, userId: string, userName: string): Promise<boolean> {
  try {
    console.log('[GroupChat] User requesting to join:', { groupId, userId, userName });
    
    // Convert entity ID to UUID if needed
    let userUuid = userId;
    if (/^\d+$/.test(userId)) {
      console.log('[GroupChat] Converting entity ID to UUID...');
      const userResult = await turso.execute({
        sql: `
          SELECT u.id as uuid_id
          FROM users u
          INNER JOIN entities e ON e.data LIKE '%' || u.email || '%'
          WHERE e.id = ? AND e.entity_type = 'User'
          LIMIT 1
        `,
        args: [userId]
      });
      
      if (userResult.rows.length > 0) {
        userUuid = userResult.rows[0].uuid_id as string;
        console.log('[GroupChat] Converted to UUID:', userUuid);
      } else {
        console.error('[GroupChat] Could not find UUID for entity ID:', userId);
        return false;
      }
    }
    
    // Check if already a member
    const memberCheck = await turso.execute({
      sql: `SELECT id FROM group_members WHERE group_id = ? AND user_id = ?`,
      args: [groupId, userUuid]
    });
    
    if (memberCheck.rows.length > 0) {
      console.log('[GroupChat] User already a member');
      return true;
    }
    
    // Check if already requested
    const requestCheck = await turso.execute({
      sql: `SELECT id FROM group_join_requests WHERE group_id = ? AND user_id = ? AND status = 'pending'`,
      args: [groupId, userUuid]
    });
    
    if (requestCheck.rows.length > 0) {
      console.log('[GroupChat] Join request already pending');
      return true;
    }
    
    // Create join request
    const requestId = `gjr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await turso.execute({
      sql: `INSERT INTO group_join_requests (id, group_id, user_id, user_name, status) VALUES (?, ?, ?, ?, 'pending')`,
      args: [requestId, groupId, userUuid, userName]
    });
    
    console.log('[GroupChat] ✅ Join request created');
    return true;
  } catch (error) {
    console.error('[GroupChat] Failed to request join:', error);
    return false;
  }
}

// Get pending join requests for groups you created
export async function getPendingJoinRequests(userId: string): Promise<JoinRequest[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT jr.* 
        FROM group_join_requests jr
        INNER JOIN groups g ON jr.group_id = g.id
        WHERE g.created_by = ? AND jr.status = 'pending'
        ORDER BY jr.created_at DESC
      `,
      args: [userId]
    });
    
    return result.rows.map((row: any) => ({
      id: row.id,
      group_id: row.group_id,
      user_id: row.user_id,
      user_name: row.user_name,
      status: row.status,
      created_at: row.created_at
    }));
  } catch (error) {
    console.error('Failed to get pending requests:', error);
    return [];
  }
}

// Approve join request
export async function approveJoinRequest(requestId: string, groupId: string, userId: string): Promise<boolean> {
  try {
    console.log('[GroupChat] Approving join request:', requestId);
    
    // Update request status
    await turso.execute({
      sql: `UPDATE group_join_requests SET status = 'approved' WHERE id = ?`,
      args: [requestId]
    });
    
    // Add user to group
    const success = await addGroupMember(groupId, userId);
    console.log('[GroupChat] ✅ Join request approved');
    return success;
  } catch (error) {
    console.error('[GroupChat] Failed to approve request:', error);
    return false;
  }
}

// Reject join request
export async function rejectJoinRequest(requestId: string): Promise<boolean> {
  try {
    await turso.execute({
      sql: `UPDATE group_join_requests SET status = 'rejected' WHERE id = ?`,
      args: [requestId]
    });
    return true;
  } catch (error) {
    console.error('Failed to reject request:', error);
    return false;
  }
}

// Get all public groups (legacy - now same as getAllPublicGroups)
export async function getAllGroups(): Promise<Group[]> {
  try {
    const result = await turso.execute({
      sql: `
        SELECT 
          g.id, 
          g.name, 
          g.created_by, 
          g.created_at,
          COUNT(DISTINCT gm.user_id) as member_count
        FROM groups g
        LEFT JOIN group_members gm ON g.id = gm.group_id
        GROUP BY g.id
        ORDER BY g.created_at DESC
      `
    });

    return result.rows.map((row: any) => ({
      id: row.id,
      name: row.name,
      created_by: row.created_by,
      created_at: row.created_at,
      is_public: true,
      member_count: Number(row.member_count) || 0
    }));
  } catch (error) {
    console.error('Failed to get all groups:', error);
    return [];
  }
}
